<template>
	<div class="columns-container">
		<div class="columns-container__section-header">
			<div class="columns-container__section-title">Columns</div>
			<Button
				v-if="!readonly"
				icon="fal fa-plus"
				label="New Column"
				severity="secondary"
				@click="createNewColumn" />
		</div>
		<Column
			v-for="column in sortedColumns"
			:key="column.name"
			:ref="(component: any) => setColumnRef(column.name, component)"
			:column
			:editing="true"
			:module
			:readonly
			@click="editColumn(column)"
			@delete="handleColumnDelete(column.name)"
			@dragend="onDragEndColumn"
			@dragstart="onDragStartColumn(column.name)" />
		<ColumnForm
			:column="newColumn ? newColumn : editingColumn"
			:editing="!newColumn"
			:module
			@cancel="handleFormClose()"
			@close="handleFormClose()"
			@save="handleFormSave" />
	</div>
</template>

<script setup lang="ts">
import type { EnrichedApp } from '@codecannon/schemas/schemas/EnrichedApp'
import { EnrichedColumn } from '@codecannon/schemas/schemas/EnrichedColumn'
import type { EnrichedModule } from '@codecannon/schemas/schemas/EnrichedModule'
import { computed, reactive, ref, type ComponentPublicInstance, type Ref } from 'vue'
import Column from './ColumnsContainer/Column.vue'
import { useConfirm } from 'primevue'
import { useDroppable } from '@/helpers/dragAndDrop/droppable'
import Button from 'primevue/button'
import ColumnForm from './ColumnsContainer/ColumnForm.vue'

const emit = defineEmits<{
	(e: 'update'): void
}>()

const props = defineProps<{
	app: EnrichedApp
	module: EnrichedModule
	readonly: boolean
}>()

const confirm = useConfirm()
const { onDragEndColumn, onDragStartColumn, setColumnRef } = useDragAndDrop()
const { editingColumn, newColumn, handleFormClose, handleFormSave, createNewColumn, editColumn } =
	useColumnForm()
const sortedColumns = computed(() =>
	Object.values(props.module.model.columns).sort((a, b) => a.order - b.order),
)

function handleColumnDelete(name: string) {
	confirm.require({
		message: `Are you sure you want to delete the ${name} column?`,
		header: 'Danger Zone',
		icon: 'fas fa-exclamation-triangle',
		rejectLabel: 'Cancel',
		rejectProps: {
			label: 'Cancel',
			severity: 'secondary',
			outlined: true,
		},
		acceptProps: {
			label: 'Delete',
			severity: 'danger',
		},
		accept: async () => {
			props.module.removeColumn(name)
			emit('update')
		},
		reject: () => {},
	})
}

function useColumnForm() {
	const newColumn = ref<EnrichedColumn | null>(null) as Ref<EnrichedColumn | null>
	const editingColumn = ref<EnrichedColumn | null>(null) as Ref<EnrichedColumn | null>

	function createNewColumn() {
		editingColumn.value = null
		newColumn.value = props.module.makeDefaultEmptyColumn()
	}

	function editColumn(column: EnrichedColumn) {
		newColumn.value = null
		editingColumn.value = column
	}

	function handleFormSave(params: {
		title: string
		subtitle: string | null
		column: EnrichedColumn
	}) {
		if (!newColumn.value && !editingColumn.value) return

		if (editingColumn.value) {
			props.module.removeColumn(editingColumn.value.name)
		}
		props.module.setColumn(params.column)
		if (params.title !== '') {
			props.module.model.title = params.title
		} else {
			props.module.model.title = params.title
		}
		props.module.model.subtitle = params.subtitle

		newColumn.value = null
		editingColumn.value = null

		emit('update')
	}

	function handleFormClose() {
		editingColumn.value = null
		newColumn.value = null
	}

	return {
		newColumn,
		editingColumn,
		handleFormSave,
		handleFormClose,
		createNewColumn,
		editColumn,
	}
}

function useDragAndDrop() {
	const draggingColumn = ref<EnrichedColumn | null>(null) as Ref<EnrichedColumn | null>
	const columnOrderChanged = ref(false)
	const columnRefs = reactive<Map<string, HTMLElement>>(new Map())
	const { onDragEnd: onDragEndColumn, onDragStart: onDragStartColumn } = useDroppable({
		dropAreas: columnRefs,
		onDragStart: (column: string) => {
			draggingColumn.value = props.module.model.columns[column]!
		},
		onAreaEnter: (column: string) => {
			if (!draggingColumn.value) return
			moveColumnTo(draggingColumn.value, props.module.model.columns[column]!.order)
		},
		onDragEnd: () => {
			if (columnOrderChanged.value) {
				emit('update')
			}
			draggingColumn.value = null
			columnOrderChanged.value = false
		},
	})

	/**
	 * Moves a column to a new position in the schema
	 *
	 * @param column The column to move
	 * @param targetOrder The new position (order) of the column
	 */
	function moveColumnTo(column: EnrichedColumn, targetOrder: number) {
		columnOrderChanged.value = true
		props.module.moveColumn(column.name, targetOrder)
	}

	function setColumnRef(columnName: string, component: ComponentPublicInstance | null) {
		if (component) {
			if (component.$el instanceof HTMLElement) {
				columnRefs.set(columnName, component.$el)
			} else {
				columnRefs.set(columnName, component.$el.nextElementSibling)
			}
		} else {
			columnRefs.delete(columnName)
		}
	}

	return {
		onDragEndColumn,
		onDragStartColumn,
		setColumnRef,
	}
}
</script>

<style lang="scss" scoped>
.columns-container {
	.columns-container__section-header {
		display: flex;
		align-items: space-between;
		width: 100%;
		margin-bottom: 10px;
		height: 42px;

		.columns-container__section-title {
			display: flex;
			align-items: center;
			font-size: 22px;
			flex: 1;
		}
	}
}
</style>
